import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
} from "@fluentui/react-components";
import type {
  DialogOpenChangeData,
  DialogOpenChangeEvent,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";

import { useLayoutStore } from "../../../store/layout.store.js";
import { cn } from "../../../utils/tailwind-class-merge.js";

export interface SharedModalProps {
  onClose?: () => void;
  onNext?: () => void;
}

interface BaseModalProps {
  title: string;
  // Callback used when the modal is dismissed. Falls back to the layout store.
  onClose?: () => void;
  // Content of the modal.
  children?: React.ReactNode;
  // Component to render as a full-bleed media panel below the title.
  mediaComponent?: React.ReactNode;
  // Component to render in the action area of the modal.
  actionComponent?: React.ReactNode;
  // Whether the action area should span the full dialog width.
  actionsFluid?: boolean;
  // Component to render in the close button area of the modal.
  closeButtonComponent?: React.ReactNode;
  // Whether to trap focus inside the modal and prevent dismissal by clicking outside or pressing escape.
  trap?: boolean;
}

interface BaseModalCloseButtonProps {
  onClose: () => void;
  component?: React.ReactNode;
  className?: string | undefined;
}

// Handles showing the close button. If there is user provided component, we'll use it.
// Otherwise, we'll show the default close button which gets passed the onClose prop.
const BaseModalCloseButton = ({
  onClose,
  component,
  className,
}: BaseModalCloseButtonProps) => {
  if (component !== undefined) {
    return component;
  }

  return (
    <Button
      appearance="subtle"
      className={cn("absolute top-6 right-6", className)}
      icon={<Dismiss24Regular />}
      onClick={onClose}
    />
  );
};

export const BaseModal = ({
  title,
  onClose,
  children,
  mediaComponent,
  actionComponent,
  actionsFluid,
  closeButtonComponent,
  trap = false,
}: BaseModalProps) => {
  const { hideModal } = useLayoutStore();
  const hasMediaComponent = mediaComponent !== undefined;
  // `!p-0` is required: FluentUI (Griffel) injects `padding: 24px` at runtime
  // after Tailwind, so a plain `p-0` loses the specificity tie. Without `!`,
  // the surface keeps its 24px padding and the media panel cannot reach the
  // dialog edges.
  const mediaSurfaceClassName = hasMediaComponent
    ? "overflow-hidden !p-0"
    : undefined;
  // `!p-0 !m-0` override Griffel's 2px padding / -2px margin focus-outline
  // trick so the media panel is truly flush.
  const mediaContentClassName = hasMediaComponent
    ? "!m-0 flex flex-col gap-4 overflow-x-hidden !p-0"
    : undefined;
  const handleClose = useStableCallback(() => {
    if (onClose) {
      onClose();
      return;
    }

    hideModal();
  });

  const handleOpenChange = useStableCallback(
    (_event: DialogOpenChangeEvent, _data: DialogOpenChangeData) => {
      // Only allow closing if trap is false
      if (!trap) {
        handleClose();
      }
    }
  );

  return (
    <Dialog
      modalType={trap ? "alert" : "modal"}
      onOpenChange={handleOpenChange}
      open={true}
    >
      <DialogSurface
        className={cn(
          "relative sm:max-h-[90lvh] sm:w-[90lvw] md:min-h-[500px] md:min-w-[800px]",
          mediaSurfaceClassName
        )}
        data-blocks-hover="true"
      >
        <DialogBody
          className={cn(
            "sm:max-h-[90lvh]",
            hasMediaComponent ? "overflow-hidden" : "flex md:h-[450px]"
          )}
        >
          <DialogTitle
            className={hasMediaComponent ? "px-6 pt-6 pr-14" : undefined}
          >
            {title}
          </DialogTitle>
          <BaseModalCloseButton
            className={hasMediaComponent ? "top-4 right-4" : ""}
            component={closeButtonComponent}
            onClose={handleClose}
          />
          <DialogContent className={mediaContentClassName}>
            {hasMediaComponent ? (
              <>
                <div className="overflow-hidden">{mediaComponent}</div>
                <div className="px-6">{children}</div>
              </>
            ) : (
              children
            )}
          </DialogContent>
          {Boolean(actionComponent) && (
            <DialogActions
              className={hasMediaComponent ? "px-6 pb-6" : undefined}
              fluid={actionsFluid ?? hasMediaComponent}
            >
              {actionComponent}
            </DialogActions>
          )}
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
